import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import BusinessEmailAddressInput from '../form-inputs/business-email-address-input/BusinessEmailAddressInput';
import BusinessPhoneNumberInput from '../form-inputs/business-phone-number-input/BusinessPhoneNumberInput';
import AccountCircleIcon from '../../../assets/svg/account_circle.svg?react';
import BrushIcon from '../../../assets/svg/brush.svg?react';

import CircularProgress from '@mui/material/CircularProgress';
import { useEmergencyContactCardForm } from './useEmergencyContactCardForm';
import styles from './EmergencyContactCard.module.scss';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import Tooltip from '@mui/material/Tooltip';
// import { SocialLink } from '../social-media-form/SocialMediaForm';
import { Employee } from '../../models/employee.model';
import { USER_PERMISSIONS } from '../constants/permissions';
import { useCheckPermission } from '../hooks/useCheckPermission';

type EmergencyContactCardProps = {
  simplified?: boolean;
  employee: Partial<Employee>;
};

export default function EmergencyContactCard({ employee }: EmergencyContactCardProps) {
  const { t } = useTranslation();
  const isLoading = false;
  const {
    formData,
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    confirmExit,
    cancelChanges,
  } = useEmergencyContactCardForm(employee);

  const checkPermission = useCheckPermission();

  const relationshipOptions = ['Friend', 'Guardian', 'Parent', 'Partner', 'Sibling'];

  return (
    <Grid container className='inputs-editable'>
      <Grid size={{ xs: 12 }}>
        <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
          <CardContent className={isLoading ? styles.card_content_min_height : ''}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Grid container alignItems='center' flexWrap='nowrap' p={1}>
                  <Grid size={{ xs: 11 }} container alignItems='center' gap={1}>
                    <Grid sx={{ marginBottom: '0.2rem' }}>
                      <AccountCircleIcon />
                    </Grid>
                    <Grid>
                      <Typography variant='h6'>Emergency Contact</Typography>
                      {/* TODO : Add translation, for some reason didn't wanna work will look later */}
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 1 }} container justifyContent='flex-end'>
                    {!isEdited ? (
                      <Tooltip
                        className='flex-shrink-0'
                        title={
                          checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE)
                            ? t('buttons.edit')
                            : t('messages.contactAdministrator')
                        }
                        placement='top'
                      >
                        <IconButton
                          onClick={toggleEditing}
                          disabled={!checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE)}
                        >
                          <BrushIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box display='flex' gap='8px' alignItems='center'>
                        <Button
                          type='button'
                          className='btn-default btn-action-basic btn-cancel'
                          role='button'
                          variant='contained'
                          size='large'
                          onClick={cancelChanges}
                        >
                          {t('buttons.cancel')}
                        </Button>
                        <Button
                          type='submit'
                          className='btn-default btn-action-basic'
                          role='button'
                          disabled={!isEdited}
                          variant='contained'
                          size='large'
                          onClick={saveChanges}
                        >
                          {t('buttons.save')}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <TextField
                          fullWidth
                          value={formData.full_name}
                          label='Full name' // TODO : Add translation
                          onChange={(e) => {
                            handleChange('full_name')(e.target.value);
                          }}
                          disabled={!isEdited}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <BusinessEmailAddressInput
                          value={formData.email_address}
                          onChange={handleChange('email_address')}
                          disabled={!isEdited}
                          label={t('labels.emailAddress')}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <BusinessPhoneNumberInput
                          value={formData.phone_number}
                          onChange={handleChange('phone_number')}
                          disabled={!isEdited}
                          label={t('labels.phoneNumber')}
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <Select
                          fullWidth
                          value={formData.relationship || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleChange('relationship')(e.target.value);
                          }}
                          disabled={!isEdited}
                          displayEmpty
                          inputProps={{ 'aria-label': t('labels.relationship') }}
                        >
                          <MenuItem value='' disabled>
                            Select Relationship
                          </MenuItem>
                          {relationshipOptions.map((option) => (
                            <MenuItem
                              key={option}
                              value={option}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {
                  <Grid container className={styles.container_margin_top} alignItems='center'>
                    <Grid>
                      {/* <Tooltip title={t('businessProfile.addSocials')} placement='top'>
                        <IconButton
                          onClick={toogleEditingSocialModal}
                          className={styles.add_social_button}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip> */}
                    </Grid>
                  </Grid>
                }
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
      {/* <AddSocialsModal
        open={showAddSocialModal}
        onClose={saveChangesSocialForm}
        onConfirm={toogleEditingSocialModal}
      /> */}
    </Grid>
  );
}
